import React, { useState, useEffect, useRef } from 'react';
// Icons removed for clean design

interface Alert {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  timestamp: string;
  expiresAt?: string;
  metadata?: any;
  actions?: AlertAction[];
  persistent?: boolean;
  isRead?: boolean;
}

interface AlertAction {
  label: string;
  action: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

interface AlertBottomBarProps {
  userId: string;
  storeId: string;
  role: string;
  deviceType?: string;
  websocketUrl?: string;
  onAlertAction?: (action: string, alert: Alert) => void;
}

const AlertBottomBar: React.FC<AlertBottomBarProps> = ({
  userId,
  storeId,
  role,
  deviceType = 'tablet',
  websocketUrl,
  onAlertAction
}) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected');
  const wsClient = useRef<any>(null);
  const alertQueue = useRef<Alert[]>([]);

  useEffect(() => {
    initializeWebSocket();
    
    // Cleanup on unmount
    return () => {
      if (wsClient.current && wsClient.current.readyState === WebSocket.OPEN) {
        wsClient.current.close();
      }
    };
  }, [userId, storeId, role]);

  useEffect(() => {
    // Auto-expire alerts
    const interval = setInterval(() => {
      const now = new Date();
      setAlerts(prevAlerts => 
        prevAlerts.filter(alert => {
          if (!alert.expiresAt) return true;
          return new Date(alert.expiresAt) > now;
        })
      );
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const initializeWebSocket = async () => {
    if (!websocketUrl) {
      console.warn('WebSocket URL not provided for AlertBottomBar');
      return;
    }

    try {
      setConnectionStatus('connecting');
      
      // Create native WebSocket connection
      const ws = new WebSocket(websocketUrl);
      
      ws.onopen = () => {
        setConnectionStatus('connected');
        
        // Send user registration message
        ws.send(JSON.stringify({
          action: 'connect',
          userId,
          storeId,
          role,
          deviceType
        }));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'alert') {
            handleNewAlert(data);
          } else if (data.type === 'broadcast') {
            handleNewAlert({
              ...data,
              type: 'broadcast',
              persistent: true
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = () => {
        setConnectionStatus('disconnected');
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          initializeWebSocket();
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('disconnected');
      };
      
      wsClient.current = ws;
      
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      setConnectionStatus('disconnected');
    }
  };

  const handleNewAlert = (alertData: any) => {
    const newAlert: Alert = {
      id: alertData.id || alertData.messageId || Date.now().toString(),
      type: alertData.type || alertData.alertType,
      title: alertData.title || getAlertTitle(alertData.alertType),
      message: alertData.message,
      priority: alertData.priority || 'medium',
      timestamp: alertData.timestamp || new Date().toISOString(),
      expiresAt: alertData.expiresAt,
      metadata: alertData.metadata || {},
      actions: alertData.actions || getDefaultActions(alertData.alertType),
      persistent: alertData.persistent || false,
      isRead: false
    };

    setAlerts(prevAlerts => {
      // Check if alert already exists
      const existingIndex = prevAlerts.findIndex(alert => alert.id === newAlert.id);
      if (existingIndex >= 0) {
        // Update existing alert
        const updated = [...prevAlerts];
        updated[existingIndex] = newAlert;
        return updated;
      } else {
        // Add new alert
        return [...prevAlerts, newAlert];
      }
    });

    // Auto-expand for high/urgent priority alerts
    if (newAlert.priority === 'high' || newAlert.priority === 'urgent') {
      setIsExpanded(true);
    }
  };

  const getAlertTitle = (alertType: string): string => {
    const titles: { [key: string]: string } = {
      'kds_ready': 'Order Ready',
      'table_seated': 'Table Seated',
      'item_86': 'Item 86\'d',
      'online_order': 'Online Order',
      'approval_request': 'Approval Needed',
      'approval_response': 'Request Response',
      'broadcast': 'Announcement'
    };
    return titles[alertType] || 'Notification';
  };

  const getDefaultActions = (alertType: string): AlertAction[] => {
    switch (alertType) {
      case 'kds_ready':
        return [
          { label: 'Acknowledge', action: 'acknowledge', variant: 'primary' },
          { label: 'Dismiss', action: 'dismiss', variant: 'secondary' }
        ];
      case 'table_seated':
        return [
          { label: 'Start Order', action: 'start_order', variant: 'primary' },
          { label: 'View Table', action: 'view_table', variant: 'secondary' }
        ];
      case 'approval_request':
        return [
          { label: 'Approve', action: 'approve', variant: 'primary' },
          { label: 'Deny', action: 'deny', variant: 'danger' },
          { label: 'Details', action: 'view_details', variant: 'secondary' }
        ];
      default:
        return [{ label: 'Dismiss', action: 'dismiss', variant: 'secondary' }];
    }
  };

  const handleAlertAction = (action: string, alert: Alert) => {
    if (action === 'dismiss') {
      dismissAlert(alert.id);
    } else if (action === 'acknowledge') {
      acknowledgeAlert(alert.id);
    } else {
      // Call custom action handler
      onAlertAction?.(action, alert);
      
      // Auto-dismiss unless persistent
      if (!alert.persistent) {
        setTimeout(() => dismissAlert(alert.id), 1000);
      }
    }
  };

  const dismissAlert = (alertId: string) => {
    setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== alertId));
    
    // Adjust current index if needed
    setCurrentAlertIndex(prevIndex => {
      const newCount = alerts.length - 1;
      if (newCount === 0) return 0;
      if (prevIndex >= newCount) return newCount - 1;
      return prevIndex;
    });
  };

  const acknowledgeAlert = (alertId: string) => {
    // Mark as read and optionally keep in list
    setAlerts(prevAlerts =>
      prevAlerts.map(alert =>
        alert.id === alertId
          ? { ...alert, isRead: true }
          : alert
      )
    );

    // Send acknowledgment to server
    if (wsClient.current && wsClient.current.readyState === WebSocket.OPEN) {
      wsClient.current.send(JSON.stringify({
        action: 'acknowledge',
        alertId,
        userId,
        timestamp: new Date().toISOString()
      }));
    }
  };

  const nextAlert = () => {
    if (alerts.length > 1) {
      setCurrentAlertIndex((prev) => (prev + 1) % alerts.length);
    }
  };

  const previousAlert = () => {
    if (alerts.length > 1) {
      setCurrentAlertIndex((prev) => (prev - 1 + alerts.length) % alerts.length);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-600';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'kds_ready': return <span className="text-sm font-bold">✓</span>;
      case 'table_seated': return <span className="text-sm font-bold">T</span>;
      case 'item_86': return <span className="text-sm font-bold">!</span>;
      case 'approval_request': return <span className="text-sm font-bold">?</span>;
      case 'broadcast': return <span className="text-sm font-bold">B</span>;
      default: return <span className="text-sm font-bold">A</span>;
    }
  };

  if (alerts.length === 0) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-2 text-gray-500">
            <span className="text-sm font-bold">A</span>
            <span className="text-sm">No active alerts</span>
          </div>
          <div className={`flex items-center space-x-1 text-xs ${
            connectionStatus === 'connected' ? 'text-green-600' : 
            connectionStatus === 'connecting' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-600' : 
              connectionStatus === 'connecting' ? 'bg-yellow-600' : 'bg-red-600'
            }`} />
            <span className="capitalize">{connectionStatus}</span>
          </div>
        </div>
      </div>
    );
  }

  const currentAlert = alerts[currentAlertIndex];

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-white border-t-2 ${getPriorityColor(currentAlert.priority)} shadow-lg transition-all duration-300 ${
      isExpanded ? 'h-32' : 'h-16'
    }`}>
      <div className="max-w-7xl mx-auto h-full">
        {/* Collapsed View */}
        <div 
          className="flex items-center justify-between h-16 px-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full text-white ${getPriorityColor(currentAlert.priority)}`}>
              {getAlertIcon(currentAlert.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{currentAlert.title}</p>
              <p className="text-sm text-gray-600 truncate">{currentAlert.message}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {alerts.length > 1 && (
              <div className="flex items-center space-x-1">
                <button
                  onClick={(e) => { e.stopPropagation(); previousAlert(); }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  ←
                </button>
                <span className="text-xs text-gray-500">
                  {currentAlertIndex + 1} of {alerts.length}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); nextAlert(); }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  →
                </button>
              </div>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); dismissAlert(currentAlert.id); }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-500">
                {new Date(currentAlert.timestamp).toLocaleTimeString()}
              </div>
              {currentAlert.expiresAt && (
                <div className="text-xs text-orange-600">
                  Expires: {new Date(currentAlert.expiresAt).toLocaleTimeString()}
                </div>
              )}
            </div>
            
            <p className="text-sm text-gray-700 mb-3">{currentAlert.message}</p>
            
            {currentAlert.actions && currentAlert.actions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {currentAlert.actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleAlertAction(action.action, currentAlert)}
                    className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${
                      action.variant === 'primary'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : action.variant === 'danger'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertBottomBar;